import axios, { AxiosError } from 'axios'
import { toast } from 'sonner'
import type { InternalAxiosRequestConfig } from 'axios'

import { API_BASE_URL } from '@/lib/config'
import { m } from '@/lib/paraglide/messages'
import { captureAuthRefresh } from '@/lib/auth-refresh-tracking'
import { trackApiError, trackApiOutcome } from '@/lib/recent-api-errors'
import { SERVER_TIME_HEADER_NAME, recordServerTime } from '@/lib/server-time'

interface RetryableRequest extends InternalAxiosRequestConfig {
    _retry?: boolean
}

export { API_BASE_URL }

// Values the backend sends so the frontend can interpolate them into the
// localized message for an error code. Never user-facing text on their own.
export type ApiErrorParams = Record<string, unknown>

export interface ApiErrorInfo {
    code: string
    // Legacy field kept during the expand-contract transition so old builds
    // keep working; new frontend code translates from `code` + `params` and
    // ignores this. See docs/decisions/error-i18n-single-owner.md.
    message: string
    params?: ApiErrorParams
    // Details có thể là validation map (Record<string,string>) hoặc structured
    // domain payload (vd: PURCHASE_INSUFFICIENT → array of insufficient details).
    details?: Record<string, string> | unknown
    // "config" khi lỗi do cấu hình sai (thiết bị/IMEI/tài khoản), không tự hết
    // khi thử lại — khác lỗi tạm thời (undefined). Xem response.ConfigError ở BE.
    kind?: string
}

// TTotals: kiểu `meta.totals` của riêng endpoint nhận `with_totals`; endpoint
// khác để mặc định `never` nên không đọc nhầm được.
export interface ApiResponse<T, TTotals = never> {
    success: boolean
    data?: T
    error?: ApiErrorInfo
    meta?: {
        page: number
        per_page: number
        total: number
        total_pages: number
        /** Chỉ có khi endpoint được yêu cầu trả tổng của cả tập kết quả. */
        totals?: TTotals
    }
}

// A status/code pair that the caller handles as a normal answer, for example
// "this order has no shipment". The call still rejects, but it is not an API
// error: `outcome` names the business event to record instead, if any. The
// outcome is sent for every matching response, so only declare one on a call
// that a person or a bounded retry makes, never on a poll.
export interface ExpectedApiError {
    status: number
    code: string
    outcome?: string
}

interface RequestOptions {
    signal?: AbortSignal
    // Tiến độ gửi body (0–100), vd tải ảnh trong trình soạn nội dung.
    onUploadProgress?: (pct: number) => void
    expected?: readonly ExpectedApiError[]
}

export class ApiError extends Error {
    constructor(
        public status: number,
        public code: string,
        message: string,
        public details?: Record<string, string> | unknown,
        // Full-UUID trace ID echoed by the server (X-Request-ID). Surfaced in
        // error toasts so users can quote it when reporting bugs — admins
        // grep server logs by req_id to pull the full call chain.
        public requestId?: string,
        // Interpolation values for the localized message keyed by `code`.
        public params?: ApiErrorParams,
        // "config" when this is a configuration mistake (never self-heals by
        // retrying) — see ApiErrorInfo.kind.
        public kind?: string,
    ) {
        super(message)
        this.name = 'ApiError'
    }
}

const instance = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
})

// Auto-set Content-Type: FormData → let browser handle, otherwise JSON
instance.interceptors.request.use((config) => {
    if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json'
    }
    return config
})

// Silent token refresh state — prevents concurrent refresh calls
let refreshPromise: Promise<boolean> | null = null

// A hung request (TCP half-open after sleep, captive portal) must settle:
// refreshPromise is deduped, so without a timeout every future refresh would
// await the same dead request forever. Exported because callers that put their
// own deadline around tryRefreshToken() must pick one LONGER than this — a
// shorter outer deadline gives up on a request that is still deduped in
// flight, so their "retries" all await the same hung call.
export const REFRESH_HTTP_TIMEOUT_MS = 10_000

export type RefreshFailureReason = 'unauthorized' | 'timeout' | 'network'

// Sticky reason of the most recent FAILED refresh (null after a success). The
// SSE health reporter reads this to say WHY the always-on screen cannot
// recover — a 401 (session dead, needs a human login) is a different incident
// from a timeout (slow network/server).
let lastRefreshFailure: RefreshFailureReason | null = null

export function getLastRefreshFailure(): RefreshFailureReason | null {
    return lastRefreshFailure
}

function classifyRefreshFailure(err: unknown): RefreshFailureReason {
    const e = err as { response?: { status?: number }; code?: string }
    const status = e?.response?.status
    if (status === 401 || status === 403) return 'unauthorized'
    if (e?.code === 'ECONNABORTED' || e?.code === 'ETIMEDOUT') return 'timeout'
    return 'network'
}

function postRefresh() {
    return axios.post(`${API_BASE_URL}/auth/refresh`, null, {
        withCredentials: true,
        timeout: REFRESH_HTTP_TIMEOUT_MS,
    })
}

// All tabs share one refresh cookie, and the server spends it on first use.
// Tabs whose access tokens expire together refresh one at a time under this
// browser-wide lock; a tab that waited while another refreshed reuses that
// result (the shared access cookie is already fresh) instead of posting.
const REFRESH_LOCK_NAME = 'erp-auth-refresh'
const REFRESHED_AT_KEY = 'erp-auth-refreshed-at'

function readRefreshedAt(): number {
    try {
        return Number(localStorage.getItem(REFRESHED_AT_KEY)) || 0
    } catch {
        return 0
    }
}

function markRefreshed() {
    try {
        localStorage.setItem(REFRESHED_AT_KEY, String(Date.now()))
    } catch {
        // Storage blocked: other tabs just refresh on their own.
    }
}

// What one refresh attempt went through, for the auth_session_refresh event.
interface RefreshTrace {
    raceRetried: boolean
    crossTabLock: boolean
    reusedOtherTab: boolean
}

async function refreshSession(trace: RefreshTrace): Promise<void> {
    try {
        await postRefresh()
    } catch (err) {
        // 409: another tab (or a browser without locks) rotated the session a
        // moment earlier and the browser now holds its fresh cookie. One retry
        // spends that cookie.
        const status = (err as { response?: { status?: number } })?.response
            ?.status
        if (status !== 409) throw err
        trace.raceRetried = true
        await postRefresh()
    }
    markRefreshed()
}

function refreshAcrossTabs(trace: RefreshTrace): Promise<void> {
    const locks =
        typeof navigator === 'undefined'
            ? undefined
            : (navigator.locks as LockManager | undefined)
    if (!locks) return refreshSession(trace)
    trace.crossTabLock = true
    const askedAt = Date.now()
    return locks.request(REFRESH_LOCK_NAME, async () => {
        if (readRefreshedAt() >= askedAt) {
            trace.reusedOtherTab = true
            return
        }
        await refreshSession(trace)
    })
}

export async function tryRefreshToken(): Promise<boolean> {
    if (refreshPromise) return refreshPromise
    const trace: RefreshTrace = {
        raceRetried: false,
        crossTabLock: false,
        reusedOtherTab: false,
    }
    refreshPromise = refreshAcrossTabs(trace)
        .then(() => {
            lastRefreshFailure = null
            captureAuthRefresh({
                outcome: trace.reusedOtherTab ? 'reused_other_tab' : 'refreshed',
                raceRetried: trace.raceRetried,
                crossTabLock: trace.crossTabLock,
            })
            return true
        })
        .catch((err: unknown) => {
            lastRefreshFailure = classifyRefreshFailure(err)
            captureAuthRefresh({
                outcome: lastRefreshFailure,
                raceRetried: trace.raceRetried,
                crossTabLock: trace.crossTabLock,
            })
            return false
        })
        .finally(() => {
            refreshPromise = null
        })
    return refreshPromise
}

// Public routes that must render for unauthenticated visitors (e.g. a customer
// scanning a traceability QR). A background 401 from the auth probe must NOT
// bounce these pages to the login screen.
const PUBLIC_PATH_PREFIXES = [
    '/login',
    '/trace/',
    '/customer-display',
    // Trang thông tin vùng trồng mà mã QR trỏ tới.
    '/grower-areas/',
    // Trang Zalo chuyển về sau khi cấp quyền OA: tự hiện bước "Đăng nhập để
    // hoàn tất" thay vì bị đẩy sang /login làm mất mã (spec 2026-10-08 §6.1 #8).
    '/settings/zalo/callback',
]

export function redirectToLogin() {
    const currentPath = window.location.pathname
    const onPublicPage = PUBLIC_PATH_PREFIXES.some(
        (p) => currentPath === p || currentPath.startsWith(p),
    )
    if (!onPublicPage) {
        window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
    }
}

// Các đường không bao giờ được đi qua nhánh 401-refresh. 401 ở đây là câu trả lời
// nghiệp vụ ("sai mật khẩu", "sai mã OTP") chứ không phải "phiên hết hạn": chạy
// refresh là thừa, và nếu refresh thành công thì interceptor sẽ GỬI LẠI đúng
// request đó — nhập sai mã một lần bị trừ hai lượt trong bộ đếm 5 lần.
export function shouldSkipTokenRefresh(url?: string) {
    if (!url) return true

    const path = url.split('?')[0]
    return [
        '/auth/login',
        '/auth/login/otp/verify',
        '/auth/login/otp/resend',
        '/auth/register',
        '/auth/refresh',
        '/auth/logout',
    ].includes(path)
}

// Response interceptor: unwrap data, handle 401 refresh
instance.interceptors.response.use(
    (res) => {
        // Track server clock skew — every response carries X-Server-Time so
        // consumers (SLA badge, countdown) can render "now" via server time.
        recordServerTime(
            res.headers?.[SERVER_TIME_HEADER_NAME] as string | undefined,
        )
        return res
    },
    async (error: AxiosError<ApiResponse<unknown>>) => {
        // Also refresh offset on error responses — the header is still stamped.
        recordServerTime(
            error.response?.headers?.[SERVER_TIME_HEADER_NAME] as
                string | undefined,
        )
        const originalRequest = error.config as RetryableRequest | undefined
        // Read-only impersonation backstop: a blocked write returns 403 with this
        // code. Show a friendly, deduped toast — do NOT redirect or trigger the
        // 401-refresh path. A stable toast id collapses background-POST repeats.
        if (
            error.response?.status === 403 &&
            error.response.data?.error?.code === 'IMPERSONATION_READ_ONLY'
        ) {
            toast.error(m.impersonation_readonly_toast(), {
                id: 'impersonation-readonly',
            })
            return Promise.reject(error)
        }
        if (
            error.response?.status === 401 &&
            originalRequest &&
            !shouldSkipTokenRefresh(originalRequest.url) &&
            !originalRequest._retry
        ) {
            originalRequest._retry = true
            const refreshed = await tryRefreshToken()
            if (refreshed) {
                return instance(originalRequest)
            }
            redirectToLogin()
        }
        return Promise.reject(error)
    },
)

async function request<T, TTotals = never>(
    method: string,
    endpoint: string,
    body?: unknown,
    params?: Record<string, string | number | boolean | undefined>,
    options?: RequestOptions,
): Promise<ApiResponse<T, TTotals>> {
    try {
        const res = await instance.request<ApiResponse<T, TTotals>>({
            method,
            url: endpoint,
            data: body,
            params,
            signal: options?.signal,
            onUploadProgress: options?.onUploadProgress
                ? (e) => {
                      if (e.total)
                          options.onUploadProgress?.(
                              Math.round((e.loaded / e.total) * 100),
                          )
                  }
                : undefined,
        })
        if (res.status === 204) {
            return { success: true } as ApiResponse<T, TTotals>
        }
        return res.data
    } catch (err) {
        // This catch runs AFTER the 401 silent-refresh interceptor, so only
        // errors that truly surface to the caller are tracked — refresh-retry
        // churn never reaches telemetry.
        if (
            (err instanceof AxiosError && err.code === 'ERR_CANCELED') ||
            axios.isCancel(err)
        ) {
            throw err
        }
        if (err instanceof AxiosError && err.response?.data?.error) {
            const info = err.response.data.error as ApiErrorInfo
            const reqId = err.response.headers?.['x-request-id'] as
                string | undefined
            const apiError = new ApiError(
                err.response.status,
                info.code || 'UNKNOWN_ERROR',
                info.message || 'An error occurred',
                info.details,
                reqId,
                info.params,
                info.kind,
            )
            const telemetry = {
                requestId: reqId,
                status: apiError.status,
                code: apiError.code,
                method,
                path: endpoint,
                message: apiError.message,
                kind: apiError.kind,
            }
            const expected = options?.expected?.find(
                (e) => e.status === apiError.status && e.code === apiError.code,
            )
            if (!expected) {
                trackApiError(telemetry)
            } else if (expected.outcome) {
                trackApiOutcome(expected.outcome, telemetry)
            }
            throw apiError
        }
        if (err instanceof AxiosError) {
            const reqId = err.response?.headers?.['x-request-id'] as
                string | undefined
            const apiError = new ApiError(
                err.response?.status ?? 0,
                'NETWORK_ERROR',
                err.message,
                undefined,
                reqId,
            )
            trackApiError({
                requestId: reqId,
                status: apiError.status,
                code: apiError.code,
                method,
                path: endpoint,
                message: apiError.message,
            })
            throw apiError
        }
        throw err
    }
}

export const api = {
    // params is typed `object` (not a generic Record) so callers can pass a
    // typed params interface while still providing an explicit response type
    // argument — with two type params, passing only <T> would otherwise force
    // TParams to its default and reject the interface (no index signature).
    // getBlob tải file nhị phân qua CÙNG instance với mọi call khác, nhờ đó
    // dùng chung baseURL và interceptor refresh 401 (axios trần sẽ bỏ qua cả
    // hai: hết phiên là tải file hỏng vĩnh viễn cho tới khi query khác gia hạn
    // hộ). Trả nguyên response để caller đọc được Content-Disposition.
    getBlob: (endpoint: string, options?: { params?: object }) =>
        instance.get<Blob>(endpoint, {
            params: options?.params,
            responseType: 'blob',
        }),

    // postBlob: như getBlob nhưng gửi body JSON (vd dàn trang báo cáo ra file).
    postBlob: (endpoint: string, body: unknown) =>
        instance.post<Blob>(endpoint, body, { responseType: 'blob' }),

    get: <T, TTotals = never>(
        endpoint: string,
        options?: RequestOptions & { params?: object },
    ) =>
        request<T, TTotals>(
            'GET',
            endpoint,
            undefined,
            options?.params as
                | Record<string, string | number | boolean | undefined>
                | undefined,
            options,
        ),

    post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
        request<T>('POST', endpoint, body, undefined, options),

    put: <T>(endpoint: string, body?: unknown) =>
        request<T>('PUT', endpoint, body),

    patch: <T>(endpoint: string, body?: unknown) =>
        request<T>('PATCH', endpoint, body),

    // config.data: body kèm DELETE (vd lý do xóa) — optional, call-site cũ giữ nguyên.
    delete: <T>(endpoint: string, config?: { data?: unknown }) =>
        request<T>('DELETE', endpoint, config?.data),
}
